import api from '../../utils/api';

export type VendorReferenceType =
  | 'languages'
  | 'specialities'
  | 'degrees'
  | 'medical-councils';

/** Vendor form catalog tabs (excludes instant-consultation specialities). */
export type VendorFormCatalogType = Exclude<VendorReferenceType, 'specialities'>;

export type ApprovalStatus = 'PENDING' | 'APPROVED' | 'REJECTED';
export type AddedByType = 'MASTER_ADMIN' | 'VENDOR';

export interface VendorReferenceItemBase {
  id: string;
  name: string;
  slug: string | null;
  doctorCategory: string | null;
  isActive: boolean;
  approvalStatus: ApprovalStatus;
  addedBy: AddedByType;
  addedById: string | null;
  reviewedByAdminId: string | null;
  reviewedAt: string | null;
  rejectionReason: string | null;
  sortOrder: number;
  createdAt: string;
  updatedAt: string;
}

export interface InstantConsultationSectionRef {
  id: string;
  title: string;
  slug: string | null;
  isActive: boolean;
}

export interface VendorReferenceSpecialityDto extends VendorReferenceItemBase {
  description: string | null;
  iconKey: string | null;
  iconPath: string | null;
  instantConsultationFee: string | number | null;
  showInInstantConsultation: boolean;
  isFeatured: boolean;
  instantConsultationSectionId: string | null;
  instantConsultationSection?: InstantConsultationSectionRef | null;
  /** Resolved from VendorReferenceInstantConsultationSection.title (API-enriched). */
  sectionTitle?: string | null;
  /** True when at least one approved active doctor clinic lists this speciality. */
  presentInDoctorClinic?: boolean;
}

export type VendorReferenceItemDto =
  | VendorReferenceItemBase
  | VendorReferenceSpecialityDto;

export interface ListVendorReferenceParams {
  approvalStatus?: ApprovalStatus;
  isActive?: boolean;
  addedBy?: AddedByType;
  search?: string;
  doctorCategory?: string;
  showInInstantConsultation?: boolean;
  /** Filter by specialities visible in instant consultation (doctor clinic profiles). */
  shownInInstant?: boolean;
  page?: number;
  limit?: number;
}

export interface PaginatedResponse<T> {
  success: boolean;
  data: T[];
  pagination?: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

const SPECIALITY_TAB = {
  type: 'specialities' as const,
  label: 'Specialities',
  description:
    'Doctor specialities for vendor profiles and the instant consultation experience (icon, fee, sections).',
};

export const VENDOR_REFERENCE_TABS: {
  type: VendorReferenceType;
  label: string;
  description: string;
}[] = [
  SPECIALITY_TAB,
  {
    type: 'languages',
    label: 'Languages',
    description: 'Languages spoken — vendor onboarding dropdown.',
  },
  {
    type: 'degrees',
    label: 'Degrees',
    description: 'Educational qualifications by doctor category.',
  },
  {
    type: 'medical-councils',
    label: 'Medical councils',
    description: 'Registration councils for license verification.',
  },
];

/** Vendor form catalog (Directory) — languages, degrees, councils only. */
export const VENDOR_FORM_CATALOG_TABS = VENDOR_REFERENCE_TABS.filter(
  (t) => t.type !== 'specialities'
);

export const INSTANT_CONSULTATION_SPECIALITY_TAB = SPECIALITY_TAB;

const basePath = (type: VendorReferenceType) =>
  `/master-admin/vendor-reference-data/${type}`;

const stripMultipartContentType = (data: FormData, headers: Record<string, string>) => {
  delete headers['Content-Type'];
  return data;
};

export const addedByLabel = (addedBy?: AddedByType | null): string =>
  addedBy === 'VENDOR' ? 'Vendor' : 'Master Admin';

export const isSpecialityItem = (
  item: VendorReferenceItemDto
): item is VendorReferenceSpecialityDto => 'showInInstantConsultation' in item;

export async function fetchVendorReferencePendingSummary(): Promise<{
  success: boolean;
  data: Record<VendorReferenceType, number>;
}> {
  const response = await api.get('/master-admin/vendor-reference-data/pending-summary');
  return response.data;
}

export async function fetchVendorReferenceItems(
  type: VendorReferenceType,
  params?: ListVendorReferenceParams
): Promise<PaginatedResponse<VendorReferenceItemDto>> {
  const response = await api.get<PaginatedResponse<VendorReferenceItemDto>>(
    basePath(type),
    { params }
  );
  return response.data;
}

export async function fetchVendorReferenceItemById(
  type: VendorReferenceType,
  id: string
): Promise<{ success: boolean; data: VendorReferenceItemDto }> {
  const response = await api.get<{ success: boolean; data: VendorReferenceItemDto }>(
    `${basePath(type)}/${encodeURIComponent(id)}`
  );
  return response.data;
}

export type CatalogFormPayload = {
  name: string;
  doctorCategory?: string | null;
  sortOrder?: number;
  isActive?: boolean;
  description?: string | null;
  iconKey?: string | null;
  instantConsultationFee?: string | number | null;
  showInInstantConsultation?: boolean;
  isFeatured?: boolean;
  instantConsultationSectionId?: string | null;
};

function appendFormFields(fd: FormData, payload: CatalogFormPayload) {
  fd.append('name', payload.name.trim());
  if (payload.doctorCategory !== undefined) {
    fd.append('doctorCategory', payload.doctorCategory?.trim() || '');
  }
  if (payload.sortOrder !== undefined) fd.append('sortOrder', String(payload.sortOrder));
  if (payload.isActive !== undefined) fd.append('isActive', String(payload.isActive));
  if (payload.description !== undefined) fd.append('description', payload.description || '');
  if (payload.iconKey !== undefined) fd.append('iconKey', payload.iconKey || '');
  if (payload.instantConsultationFee !== undefined && payload.instantConsultationFee !== '') {
    fd.append('instantConsultationFee', String(payload.instantConsultationFee));
  }
  if (payload.showInInstantConsultation !== undefined) {
    fd.append('showInInstantConsultation', String(payload.showInInstantConsultation));
  }
  if (payload.isFeatured !== undefined) {
    fd.append('isFeatured', String(payload.isFeatured));
  }
  if (payload.instantConsultationSectionId !== undefined) {
    fd.append('instantConsultationSectionId', payload.instantConsultationSectionId || '');
  }
}

export async function createVendorReferenceItem(
  type: VendorReferenceType,
  payload: CatalogFormPayload,
  iconFile?: File | null
): Promise<{ success: boolean; data: VendorReferenceItemDto }> {
  if (type === 'specialities') {
    const fd = new FormData();
    appendFormFields(fd, payload);
    if (iconFile) fd.append('icon', iconFile);
    const response = await api.post(basePath(type), fd, {
      transformRequest: [
        (data, headers) => stripMultipartContentType(data as FormData, headers),
      ],
    });
    return response.data;
  }
  const response = await api.post(basePath(type), payload);
  return response.data;
}

export async function updateVendorReferenceItem(
  type: VendorReferenceType,
  id: string,
  payload: CatalogFormPayload,
  iconFile?: File | null
): Promise<{ success: boolean; data: VendorReferenceItemDto }> {
  if (type === 'specialities') {
    const fd = new FormData();
    appendFormFields(fd, payload);
    if (iconFile) fd.append('icon', iconFile);
    const response = await api.patch(`${basePath(type)}/${id}`, fd, {
      transformRequest: [
        (data, headers) => stripMultipartContentType(data as FormData, headers),
      ],
    });
    return response.data;
  }
  const response = await api.patch(`${basePath(type)}/${id}`, payload);
  return response.data;
}

export async function setVendorReferenceApproval(
  type: VendorReferenceType,
  id: string,
  body: {
    approvalStatus: ApprovalStatus;
    rejectionReason?: string;
    isActive?: boolean;
  }
): Promise<{ success: boolean; data: VendorReferenceItemDto }> {
  const response = await api.patch(`${basePath(type)}/${id}/approval`, body);
  return response.data;
}

export async function setVendorReferenceActive(
  type: VendorReferenceType,
  id: string,
  isActive: boolean
): Promise<{ success: boolean; data: VendorReferenceItemDto }> {
  const response = await api.patch(`${basePath(type)}/${id}/active`, { isActive });
  return response.data;
}

export async function deleteVendorReferenceItem(
  type: VendorReferenceType,
  id: string
): Promise<{ success: boolean }> {
  const response = await api.delete(`${basePath(type)}/${id}`);
  return response.data;
}

export interface CatalogExcelImportResult {
  createdCount: number;
  updatedCount?: number;
  skippedCount: number;
  errorCount: number;
  rowsScanned?: number;
  rowsWithName?: number;
  warning?: string;
  created: { row: number; name: string; id?: string }[];
  updated?: { row: number; name: string; id?: string }[];
  skipped: { row: number; name: string; reason: string }[];
  errors: { row: number; name: string; message: string }[];
  meta?: {
    sheetName?: string;
    headerRowNumber?: number;
    lastRowScanned?: number;
    sheetMaxRow?: number;
    resolvedColumns?: Record<string, number | null>;
    headersFound?: string[];
  };
}

export type SpecialityExcelImportResult = CatalogExcelImportResult;

const parseExcelImportResponse = (body: {
  success?: boolean;
  data?: CatalogExcelImportResult;
  message?: string;
  debug?: unknown;
}) => {
  if (!body?.success) {
    const err = new Error(body?.message || 'Excel import failed.') as Error & {
      response?: { data?: { message?: string; debug?: unknown } };
    };
    err.response = { data: { message: body?.message, debug: body?.debug } };
    throw err;
  }
  if (!body.data || typeof body.data.createdCount !== 'number') {
    const err = new Error('Invalid import response from server.') as Error & {
      response?: { data?: { message?: string; debug?: unknown } };
    };
    err.response = {
      data: {
        message: 'Invalid import response from server.',
        debug: { raw: body },
      },
    };
    throw err;
  }
  return { success: true as const, data: body.data, debug: body.debug };
};

export async function uploadVendorReferenceCatalogExcel(
  type: VendorFormCatalogType,
  file: File
): Promise<{ success: boolean; data: CatalogExcelImportResult; debug?: unknown }> {
  const fd = new FormData();
  fd.append('file', file);
  const response = await api.post<{
    success: boolean;
    data?: CatalogExcelImportResult;
    message?: string;
    debug?: unknown;
  }>(`/master-admin/vendor-reference-data/${type}/import-excel`, fd, {
    transformRequest: [
      (data, headers) => stripMultipartContentType(data as FormData, headers),
    ],
  });
  return parseExcelImportResponse(response.data);
}

const triggerBlobDownload = (blob: Blob, fileName: string) => {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = fileName;
  anchor.rel = 'noopener';
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(url);
};

const parseDownloadFileName = (
  contentDisposition: string | undefined,
  fallback: string
): string => {
  if (!contentDisposition) return fallback;
  const match = /filename="?([^";\n]+)"?/i.exec(contentDisposition);
  return match?.[1]?.trim() || fallback;
};

/** Row count is embedded in export filename when CORS blocks custom headers */
const parseRowCountFromExportFileName = (fileName: string): number | null => {
  const match = /catalog-(\d+)-rows/i.exec(fileName);
  if (!match) return null;
  const n = Number(match[1]);
  return Number.isFinite(n) ? n : null;
};

const resolveExportRowCount = (
  headers: Record<string, unknown>,
  fileName: string
): number => {
  const fromHeader = Number(
    headers['x-export-row-count'] ?? headers['X-Export-Row-Count'] ?? NaN
  );
  if (Number.isFinite(fromHeader) && fromHeader >= 0) return fromHeader;
  const fromName = parseRowCountFromExportFileName(fileName);
  return fromName ?? 0;
};

export async function downloadVendorReferenceCatalogExcel(
  type: VendorFormCatalogType
): Promise<{ rowCount: number; fileName: string }> {
  const fallback = `vedika-${type}-catalog.xlsx`;
  const response = await api.get(
    `/master-admin/vendor-reference-data/${type}/export-excel`,
    { responseType: 'blob' }
  );
  const blob = response.data as Blob;
  const fileName = parseDownloadFileName(
    response.headers['content-disposition'] as string | undefined,
    fallback
  );
  const rowCount = resolveExportRowCount(
    response.headers as Record<string, unknown>,
    fileName
  );
  triggerBlobDownload(blob, fileName);
  return { rowCount, fileName };
}

export async function downloadSpecialitiesCatalogExcel(): Promise<{
  rowCount: number;
  fileName: string;
}> {
  const fallback = 'vedika-specialities-catalog.xlsx';
  const response = await api.get(
    '/master-admin/vendor-reference-data/specialities/export-excel',
    { responseType: 'blob' }
  );
  const blob = response.data as Blob;
  const fileName = parseDownloadFileName(
    response.headers['content-disposition'] as string | undefined,
    fallback
  );
  const rowCount = resolveExportRowCount(
    response.headers as Record<string, unknown>,
    fileName
  );
  triggerBlobDownload(blob, fileName);
  return { rowCount, fileName };
}

export async function uploadSpecialitiesExcel(
  file: File
): Promise<{ success: boolean; data: SpecialityExcelImportResult; debug?: unknown }> {
  const fd = new FormData();
  fd.append('file', file);
  const response = await api.post<{
    success: boolean;
    data?: SpecialityExcelImportResult;
    message?: string;
    debug?: unknown;
  }>('/master-admin/vendor-reference-data/specialities/import-excel', fd, {
    transformRequest: [
      (data, headers) => stripMultipartContentType(data as FormData, headers),
    ],
  });
  return parseExcelImportResponse(response.data);
}
